import React, { useState } from 'react';
import { Pannellum } from 'pannellum-react';
import { TOUR_ROOMS, TOUR_ROOM_IDS } from '../data/tourRooms';

const TourViewer = ({ room, onRoomChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);

  const rooms = TOUR_ROOMS;
  const currentRoom = rooms[room] || rooms.living;

  const handleLoad = () => {
    setIsLoading(false);
    setTimeout(() => setShowInstructions(false), 5000);
  };

  const handleRoomSwitch = (roomId) => {
    setIsLoading(true);
    onRoomChange(roomId);
  };

  return (
    <div className="relative w-full h-full bg-stone-900 rounded-2xl overflow-hidden">
      {/* Loading Spinner */}
      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-stone-900">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"
                 style={{borderTopColor: 'transparent', borderColor: '#C8A96A'}}></div>
            <p className="text-white text-lg">Loading 360° View...</p>
          </div>
        </div>
      )}

      {/* Instructions Overlay */}
      {showInstructions && !isLoading && (
        <div className="absolute top-4 left-4 z-10 bg-black/70 text-white p-4 rounded-lg max-w-xs">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-lg mt-1" style={{color: '#C8A96A'}}>
              info
            </span>
            <div>
              <h4 className="font-semibold mb-2">How to Explore</h4>
              <ul className="text-sm space-y-1">
                <li>• Drag to rotate view</li>
                <li>• Scroll to zoom in/out</li>
                <li>• Click fullscreen for immersive view</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* 360° Panorama Viewer */}
      <Pannellum
        key={currentRoom.id}
        id={`tour-panorama-${currentRoom.id}`}
        image={currentRoom.image}
        width="100%"
        height="100%"
        autoLoad={true}
        onLoad={handleLoad}
        compass={true}
        northOffset={240}
        title={currentRoom.name}
        author="Jatin Interiors"
        autoRotate={-2}
        autoRotateInactivityDelay={10000}
        mouseZoom={true}
        showZoomCtrl={true}
        showFullscreenCtrl={true}
        showControls={true}
        keyboardZoom={true}
        draggable={true}
        disableKeyboardCtrl={false}
        pitch={0}
        yaw={0}
        hfov={110}
        minHfov={50}
        maxHfov={120}
      />

      {/* Room Selector */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 bg-black/70 backdrop-blur-sm rounded-full p-2 flex gap-2">
        {TOUR_ROOM_IDS.map((id) => {
          const roomData = rooms[id];
          return (
          <button
            key={roomData.id}
            onClick={() => handleRoomSwitch(roomData.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              currentRoom.id === roomData.id
                ? 'text-white'
                : 'text-stone-300 hover:text-white'
            }`}
            style={{
              backgroundColor: currentRoom.id === roomData.id ? '#C8A96A' : 'transparent'
            }}
          >
            {roomData.name}
          </button>
          );
        })}
      </div>

      {/* Room Info */}
      <div className="absolute top-4 right-4 z-10 bg-black/70 backdrop-blur-sm text-white p-4 rounded-lg max-w-xs">
        <h3 className="font-semibold mb-1" style={{color: '#C8A96A'}}>{currentRoom.name}</h3>
        <p className="text-sm text-stone-300">{currentRoom.description}</p>
      </div>

      {/* Custom Controls Overlay */}
      <div className="absolute top-1/2 right-4 transform -translate-y-1/2 z-10 flex flex-col gap-2">
        <button
          className="w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          title="Zoom In"
        >
          <span className="material-symbols-outlined text-lg">add</span>
        </button>
        <button
          className="w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          title="Zoom Out"
        >
          <span className="material-symbols-outlined text-lg">remove</span>
        </button>
        <button
          className="w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          title="Fullscreen"
        >
          <span className="material-symbols-outlined text-lg">fullscreen</span>
        </button>
      </div>
    </div>
  );
};

export default TourViewer;
